import { Translation } from '@ps/web-shared-components';
import React from 'react';

import type { FootballFixture } from '@swf/generated/graphql/scoreboards/schemas';
import { Icon } from '@swf/sports/components';

import EventStatusMessage from '../../EventStatusMessage/EventStatusMessage';

import { TXTWCL_Extra_time___N0 } from './FootballScoreboardInPlayStatus.messages.json';
import styles from './FootballScoreboardInPlayStatus.scss';

const GameTime: React.FC<{ children: React.ReactElement }> = ({ children }) => (
  <div role="timer" className={styles.text}>
    <Icon name="in-play" size="lg" />
    {children}
  </div>
);

interface Props {
  footballFixture: FootballFixture;
}

const FootballScoreboardInPlayStatus: React.FC<Props> = ({ footballFixture }) => {
  const { score, duration, penaltyShootout, status } = footballFixture;
  if (status === 'SUSPENDED') {
    return <EventStatusMessage messageId="PLAY_SUSPENDED" iconName="break" size="XSmall" />;
  }

  if (status === 'POSTPONED') {
    return <EventStatusMessage messageId="POSTPONED" iconName="finished" size="XSmall" />;
  }

  if (!score) {
    return <EventStatusMessage messageId="IN_PLAY" iconName="in-play" size="XSmall" />;
  }

  if (penaltyShootout) {
    return <EventStatusMessage messageId="PENALTIES" iconName="in-play" size="XSmall" />;
  }

  if (!duration) {
    return <EventStatusMessage messageId="IN_PLAY" iconName="in-play" size="XSmall" />;
  }

  switch (duration.status) {
    case 'INPLAY_FIRST_HALF':
    case 'INPLAY_SECOND_HALF': {
      if (duration.period === 'EXTRA') {
        if (duration.clock) {
          const fullHalfMinutes = duration.status === 'INPLAY_FIRST_HALF' ? 105 : 120;
          if (duration.clock.roundMinute > fullHalfMinutes) {
            return (
              <GameTime>
                <Translation
                  {...TXTWCL_Extra_time___N0}
                  values={{
                    0: `${fullHalfMinutes}’ +${duration.clock.roundMinute - fullHalfMinutes}`
                  }}
                />
              </GameTime>
            );
          }

          return (
            <GameTime>
              <Translation
                {...TXTWCL_Extra_time___N0}
                values={{
                  0: `${duration.clock.roundMinute}’`
                }}
              />
            </GameTime>
          );
        }

        return <EventStatusMessage messageId="EXTRA_TIME" iconName="in-play" size="XSmall" />;
      }

      if (duration.clock) {
        const fullHalfMinutes = duration.status === 'INPLAY_FIRST_HALF' ? 45 : 90;
        if (duration.clock.roundMinute > fullHalfMinutes) {
          return (
            <GameTime>
              <span>{`${fullHalfMinutes}’ +${duration.clock.roundMinute - fullHalfMinutes}`}</span>
            </GameTime>
          );
        }
        return (
          <GameTime>
            <span>{`${duration.clock.roundMinute}’`}</span>
          </GameTime>
        );
      }

      return <EventStatusMessage messageId="IN_PLAY" iconName="in-play" size="XSmall" />;
    }
    case 'HALF': {
      if (duration.period === 'EXTRA') {
        return (
          <EventStatusMessage messageId={'EXTRA_TIME_HALF_TIME'} iconName="in-play" size="XSmall" />
        );
      }
      return <EventStatusMessage messageId="HALF_TIME" iconName="in-play" size="XSmall" />;
    }
    case 'FULL':
    case 'END':
      return <EventStatusMessage messageId="FULL_TIME" iconName="finished" size="XSmall" />;
    default:
      return <EventStatusMessage messageId="IN_PLAY" iconName="in-play" size="XSmall" />;
  }
};

export default FootballScoreboardInPlayStatus;
